import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'
import { useInfiniteQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import noSubscriptions from 'assets/images/no-subscriptions.png'
import { IconSettingsOutline } from 'assets/svg/sprite'
import LinkTabs from 'components/UI/LinkTabs/LinkTabs'
import LoadingDots from 'components/UI/LoadingDots/LoadingDots'
import UserCardTransactions from 'components/UI/UserCard/UserCardTransactions'
import { usePageTitle } from 'context/pageTitleContext'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import { getLink } from 'helpers/appLinks'
import { useDebounce, useFilterQuery } from 'helpers/hooks'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { getTransactions as fetchTransactions } from 'services/endpoints/transaction'
import { useTransactionCacheStore } from 'store/transactionCacheStore'
import { type TransactionFilter } from 'types/api/transactions'
import { type ITransaction, type ITransactionsInfinitePage } from 'types/interfaces/ITypes'


export const Transactions = () => {
  const { setTitle } = usePageTitle()
  const [searchTerm, setSearchTerm] = useState('')
  const [activeFilter, setActiveFilter] = useState('all')
  const [transactions, setTransactionsState] = useState<ITransaction[]>([])
  const navigate = useNavigate()
  const { t } = useTranslation()

  const filter = useFilterQuery()
  const type = filter.get('type')
  const { ref: lastNotificationRef, inView } = useInView({ threshold: 0.1 })
  const searchDebounce = useDebounce(searchTerm, 500)
  const cacheKey = `${activeFilter}:${searchDebounce || ''}`

  const { getTransactions, setTransactions } = useTransactionCacheStore()
  const cached = getTransactions(cacheKey)


  useEffect(() => {
    setTitle(t('transactions'))
  }, [setTitle])

  // Load cached transactions on mount or filter change
  useEffect(() => {
    if (cached) {
      setTransactionsState(cached)
    } else {
      setTransactionsState([])
    }
  }, [cacheKey, cached])

  const {
    data: transactionsData,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  } = useInfiniteQuery<ITransactionsInfinitePage, Error>(
    ['transactions', activeFilter, searchDebounce],
    async ({ pageParam = 1 }) =>
      await fetchTransactions({
        filter: activeFilter as TransactionFilter,
        currentPage: pageParam,
        ...(searchDebounce && { searchTerm: searchDebounce })
      }),
    {
      getNextPageParam: lastPage => {
        const nextPage = lastPage.data.transactions.current_page + 1
        return nextPage <= lastPage.data.transactions.last_page
          ? nextPage
          : undefined
      },
      enabled: true,
      keepPreviousData: true,
      refetchOnWindowFocus: false
    }
  )

  // Update Zustand and state with fresh results
  useEffect(() => {
    if (transactionsData) {
      const flat = transactionsData.pages.flatMap(
        page => page.data.transactions.data
      )

      setTransactionsState(flat)
      setTransactions(cacheKey, flat)
    }
  }, [transactionsData, cacheKey, setTransactions])

  useEffect(() => {
    if (type) {
      setActiveFilter(type)
    } else {
      setActiveFilter('all')
    }
    setSearchTerm('')
  }, [type])

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <>
      <WithHeaderSection
        headerSection={
          <LayoutHeader
            title={t('transactions')}
            type='search-with-buttons'
            searchValue={searchTerm}
            searchFn={setSearchTerm}
            clearFn={() => setSearchTerm('')}
            additionalProps={{
              placeholder: t('searchTransactions')
            }}
            buttons={[
              {
                type: 'cta',
                icon: <IconSettingsOutline color='#fff' />,
                action: () => navigate(getLink.fanSettingsPayment())
              }
            ]}
          />
        }
        customClass='transactions'
      >
        <LinkTabs
          route='/transactions'
          filters={[
            { value: 'all', label: t('all') },
            { value: 'card', label: t('paymentCard') },
            { value: 'wallet', label: t('wallet') },
            { value: 'error', label: t('errors') }
          ]}
          activeFilter={activeFilter}
        />

        <TransactionsList>
          {transactions.map((transaction, index) => (
            <div
              key={transaction.orderId}
              ref={
                transactions.length === index + 1
                  ? lastNotificationRef
                  : undefined
              }
            >
              <UserCardTransactions transaction={transaction} />
            </div>
          ))}

          <LoadingDots visible={isFetchingNextPage} />

          {!transactions.length && !isFetching && (
            <div className='creatorsList__empty'>
              <img src={noSubscriptions} alt={t('NoTransactions')} />
              <div className='creatorsList__empty__text'>
                <div className='creatorsList__empty__text--title'>
                  {t('NoTransactions')}
                </div>
              </div>
            </div>
          )}
        </TransactionsList>
      </WithHeaderSection>
    </>
  )
}

const TransactionsList = styled.div`
    margin-top: 2rem;
      flex: 1 1 auto;
  min-height: 0;
  background-color:transparent;

`
